import { runtime } from "@/config/runtime";
import { tokenStore, type SessionTokens } from "@/services/token-store";

type ApiSurface = "account" | "public";

export interface ApiEnvelope<T> {
  success: boolean;
  data: T;
  meta?: {
    correlation_id?: string;
    [key: string]: unknown;
  };
}

interface ApiErrorPayload {
  error?: {
    code?: string;
    message?: string;
    details?: unknown;
  };
  message?: string;
}

export class ApiError extends Error {
  constructor(
    message: string,
    public readonly status: number,
    public readonly code = "API_ERROR",
    public readonly details?: unknown,
    public readonly correlationId?: string,
  ) {
    super(message);
    this.name = "ApiError";
  }
}

let refreshPromise: Promise<SessionTokens> | null = null;

function urlFor(surface: ApiSurface, path: string): string {
  const base =
    surface === "account" ? runtime.accountApiUrl : runtime.publicApiUrl;
  return `${base}/${path.replace(/^\/+/, "")}`;
}

function correlationId(): string {
  return crypto.randomUUID();
}

async function decode<T>(response: Response): Promise<ApiEnvelope<T>> {
  const body = (await response
    .json()
    .catch(() => null)) as ApiEnvelope<T> | null;

  if (response.ok && body?.success) {
    return body;
  }

  const data = body?.data as ApiErrorPayload | undefined;
  throw new ApiError(
    data?.error?.message ??
      data?.message ??
      `Request failed (${response.status}).`,
    response.status,
    data?.error?.code ?? "API_ERROR",
    data?.error?.details,
    body?.meta?.correlation_id,
  );
}

async function refreshSession(): Promise<SessionTokens> {
  const currentTokens = tokenStore.get();
  const currentRefreshToken = currentTokens?.refreshToken;

  if (!currentRefreshToken) {
    tokenStore.clear();
    window.dispatchEvent(new Event("bizconnectpay:session-expired"));
    throw new ApiError(
      "No active refresh token available.",
      401,
      "UNAUTHENTICATED",
    );
  }

  if (!refreshPromise) {
    refreshPromise = fetch(urlFor("account", "/auth/refresh"), {
      method: "POST",
      headers: {
        Accept: "application/json",
        "Content-Type": "application/json",
        "X-Correlation-ID": correlationId(),
        ...(currentTokens?.accessToken
          ? { Authorization: `Bearer ${currentTokens.accessToken}` }
          : {}),
      },
      body: JSON.stringify({
        refresh_token: currentRefreshToken,
      }),
    })
      .then((response) => decode<Record<string, unknown>>(response))
      .then(({ data }) => {
        const tokens: SessionTokens = {
          accessToken: String(data.access_token ?? ""),
          refreshToken:
            (data.refresh_token as string | undefined) ?? currentRefreshToken,
        };

        if (!tokens.accessToken) {
          throw new ApiError(
            "The refresh response did not contain tokens.",
            502,
          );
        }

        tokenStore.set(tokens);
        return tokens;
      })
      .finally(() => {
        refreshPromise = null;
      });
  }

  return refreshPromise;
}

export interface ApiRequestOptions extends Omit<RequestInit, "body"> {
  body?: unknown;
  authenticated?: boolean;
  retryOnUnauthorized?: boolean;
}

export async function apiRequest<T>(
  surface: ApiSurface,
  path: string,
  options: ApiRequestOptions = {},
): Promise<ApiEnvelope<T>> {
  const {
    authenticated = surface === "public",
    retryOnUnauthorized = true,
    body,
    headers: requestedHeaders,
    ...requestOptions
  } = options;
  const headers = new Headers(requestedHeaders);
  for (const key of [
    "x-user-id",
    "x-account-id",
    "x-account-type",
    "x-user-roles",
    "x-roles",
    "x-permissions",
    "x-country-scopes",
    "x-internal-token",
    "x-service-token",
  ])
    headers.delete(key);
  headers.set("Accept", "application/json");
  headers.set("X-Correlation-ID", correlationId());

  if (authenticated) {
    const token = tokenStore.get()?.accessToken;

    if (!token) {
      throw new ApiError(
        "Authentication is required.",
        401,
        "AUTHENTICATION_REQUIRED",
      );
    }

    headers.set("Authorization", `Bearer ${token}`);
  }

  let requestBody = body as BodyInit | undefined;

  if (
    body &&
    !(body instanceof FormData) &&
    !(body instanceof URLSearchParams)
  ) {
    headers.set("Content-Type", "application/json");
    requestBody = JSON.stringify(body);
  }

  const response = await fetch(urlFor(surface, path), {
    cache: "no-store",
    ...requestOptions,
    headers,
    body: requestBody,
  });

  if (response.status === 401 && authenticated && retryOnUnauthorized) {
    try {
      const tokens = await refreshSession();
      headers.set("Authorization", `Bearer ${tokens.accessToken}`);
      const retried = await fetch(urlFor(surface, path), {
        ...requestOptions,
        headers,
        body: requestBody,
      });
      if (retried.status === 401) {
        const cloned = retried.clone();
        const text = await cloned.text().catch(() => "");
        const is2FaChallengeError =
          text.includes("two-factor") ||
          text.includes("code") ||
          text.includes("sécurité");
        if (!is2FaChallengeError) {
          tokenStore.clear();
          window.dispatchEvent(new Event("bizconnectpay:session-expired"));
        }
      }
      return decode<T>(retried);
    } catch (error) {
      tokenStore.clear();
      window.dispatchEvent(new Event("bizconnectpay:session-expired"));
      throw error;
    }
  }

  return decode<T>(response);
}

export async function accountApi<T>(path: string, options?: ApiRequestOptions) {
  const result = await apiRequest<T>("account", path, {
    authenticated: options?.authenticated ?? false,
    ...options,
  });
  if (path === "/auth/password/reset") {
    tokenStore.clear();
    window.dispatchEvent(new Event("bizconnectpay:session-expired"));
  }
  return result;
}

export const publicApi = <T>(path: string, options?: ApiRequestOptions) =>
  apiRequest<T>("public", path, { ...options, authenticated: true });
